/**
 * Shared fetch + ApiResponse unwrap used by HttpRepository methods.
 */

import { ApiResponseSchema } from '@meridian/contracts';
import { DatasetNotFoundError, type DatasetResource } from '@meridian/core';

import { DataError, mapApiErrorCode } from './errors';

import type { z } from 'zod';

function requestCredentials(url: string): RequestCredentials {
  if (typeof window === 'undefined') return 'same-origin';
  return new URL(url, window.location.href).origin === window.location.origin
    ? 'same-origin'
    : 'include';
}

function unwrapEnvelope<T>(
  res: Response,
  body: unknown,
  schema: z.ZodType<T>,
  notFound: { readonly resource: DatasetResource; readonly id: string } | undefined,
): T {
  const envelope = ApiResponseSchema(schema).safeParse(body);
  if (!envelope.success)
    throw new DataError('VALIDATION', 'Response failed schema validation', envelope.error);
  const value = envelope.data;
  if (value.success) {
    if (!res.ok) throw new DataError('UNKNOWN', `Unexpected HTTP status (${String(res.status)})`);
    return value.data;
  }
  const code = mapApiErrorCode(value.error.code);
  if (value.error.code === 'NOT_FOUND' && notFound !== undefined) {
    throw new DatasetNotFoundError(notFound.resource, notFound.id);
  }
  if (value.error.code === 'SECURITY_DELISTED') {
    throw new DataError(code, value.error.message, undefined, {
      ...(value.error.canonicalTicker === undefined
        ? {}
        : { canonicalTicker: value.error.canonicalTicker }),
      ...(value.error.lastSeenOn === undefined ? {} : { lastSeenOn: value.error.lastSeenOn }),
    });
  }
  throw new DataError(code, value.error.message);
}

async function request(
  fetchImpl: typeof fetch,
  url: string,
  cache?: RequestCache,
): Promise<{ readonly res: Response; readonly body: unknown }> {
  let res: Response;
  try {
    res = await fetchImpl(url, {
      credentials: requestCredentials(url),
      headers: { Accept: 'application/json' },
      ...(cache === undefined ? {} : { cache }),
    });
  } catch (cause) {
    throw new DataError('NETWORK', 'Network request failed', cause);
  }

  let body: unknown;
  try {
    body = await res.json();
  } catch (cause) {
    throw new DataError('UNKNOWN', `Non-JSON response (${String(res.status)})`, cause);
  }
  return { res, body };
}

/**
 * A schema mismatch — and ONLY a schema mismatch. A body that fails its schema
 * is the one failure a re-fetch can plausibly fix, because the most likely
 * cause is that the browser served a body cached before a deploy changed the
 * representation. Network, non-JSON and API-error failures are not retried:
 * repeating them buys nothing and doubles the load on an already-failing path.
 */
function isSchemaMismatch(error: unknown): boolean {
  return error instanceof DataError && error.code === 'VALIDATION';
}

export async function httpGetJson<T>(
  fetchImpl: typeof fetch,
  url: string,
  schema: z.ZodType<T>,
  notFound?: { readonly resource: DatasetResource; readonly id: string },
): Promise<T> {
  const first = await request(fetchImpl, url);
  try {
    return unwrapEnvelope(first.res, first.body, schema, notFound);
  } catch (error) {
    if (!isSchemaMismatch(error)) throw error;
    /*
     * Reads are `private, max-age=300` (ADR-0013), so for five minutes after a
     * deploy that bumps a representation version the browser serves a FRESH
     * pre-cutover body without revalidating — `If-None-Match` is never sent and
     * the new ETag never gets a chance to miss. The old body then fails its
     * schema here and the screen shows an error state until the entry ages out.
     *
     * `cache: 'reload'` bypasses the HTTP cache for exactly one retry, turning
     * that five-minute window into one extra request. This is deliberately a
     * property of the TRANSPORT, not of any one schema: it costs nothing on the
     * happy path, needs no per-field `.default()` weakening the contract, and
     * covers every future representation bump rather than this one only.
     */
    const fresh = await request(fetchImpl, url, 'reload');
    return unwrapEnvelope(fresh.res, fresh.body, schema, notFound);
  }
}

export function buildQuery(params: Record<string, string | number | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined) continue;
    sp.set(k, String(v));
  }
  const q = sp.toString();
  return q.length > 0 ? `?${q}` : '';
}
